import { type FetchUserTransactionHistorySummaryByTypeParams, useIntl } from '@capsicum/common';
import { Image, List, type ListMethods, Text, useElementBorderBoxBlockSize, useGoatImage } from '@capsicum/common-web';
import { useInfiniteTransactionHistory } from '@capsicum/features/wallet/shared';
import { usePartners } from '@capsicum/socios-core';
import { type ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTheme } from 'styled-components';

import * as S from './styles';
import { TransactionCardsContext } from './TransactionCardsContext';
import { TransactionCardsRow } from './TransactionCardsRow';
import { TransactionCardsSkeleton } from './TransactionCardsSkeleton';

type Props = FetchUserTransactionHistorySummaryByTypeParams;

const TransactionCardsByDay = ({ type }: Props) => {
	const tooEarlyGoatImage = useGoatImage('tooEarly');
	const { $t } = useIntl();
	const { spacings } = useTheme();
	const { getPartnerBy } = usePartners();

	// TODO: Handle API errors with an error message
	const {
		canLoadMorePages: showLoadingIndicator,
		dataForList,
		fetchMoreTransactions,
		isFetchingFirstDate
	} = useInfiniteTransactionHistory({
		type
	});

	const isItemLoaded = useCallback((index: number) => index < dataForList.length, [dataForList]);

	const getItemSize = useCallback(
		(index: number): number => {
			const item = dataForList[index];
			const isLoadingIndicator = index === dataForList.length;

			switch (item?.type) {
				case 'header':
					return S.HEADER_HEIGHT;

				case 'transaction':
					return item.transaction?.isPending ? S.LARGE_CARD_HEIGHT : S.CARD_HEIGHT;
			}

			if (isLoadingIndicator) {
				return S.LOADING_INDICATOR_HEIGHT;
			}

			// Unrecognized item type
			return 0;
		},
		[dataForList]
	);

	const contextForRows = useMemo(
		() => ({
			dataForList,
			getPartnerByShortName: (name: string | undefined) => getPartnerBy(({ shortName }) => name === shortName)
		}),
		[dataForList, getPartnerBy]
	);

	const [listContainerRef, listContainerHeight] = useElementBorderBoxBlockSize();

	const listRef = useRef<ListMethods>(null);
	const rowsPlusLoadingIndicator = dataForList.length + (showLoadingIndicator ? 1 : 0);

	useEffect(
		// Before rendering newly loaded data, we reset what has up until now been the loading indicator item since
		// it's either being removed (reached end of list) or replaced with a normal item.
		// This is necessary to reset the height from getItemSize() which has been cached.
		() => {
			if (showLoadingIndicator) {
				const indicatorIndex = rowsPlusLoadingIndicator - 1;

				return () => listRef.current?.resetAfterIndex(indicatorIndex);
			}
		},
		[rowsPlusLoadingIndicator, showLoadingIndicator]
	);

	let list: ReactNode;

	if (isFetchingFirstDate) {
		list = <TransactionCardsSkeleton />;
	} else if (dataForList.length === 0) {
		list = (
			<S.NoDataContainer>
				<Image alt="tooEarlyGoatImage" height={220} src={tooEarlyGoatImage} width={300} />
				<S.NoDataTitle variant="text2">{$t('wallet.transactionHistory.noTransactions')}</S.NoDataTitle>
				<Text variant="text4">{$t('wallet.transactionHistory.noTransactionsDescription')}</Text>
			</S.NoDataContainer>
		);
	} else {
		list = (
			<TransactionCardsContext.Provider value={contextForRows}>
				<List
					height={listContainerHeight}
					isItemLoaded={isItemLoaded}
					itemCount={rowsPlusLoadingIndicator}
					itemSize={getItemSize}
					loadMoreItems={fetchMoreTransactions}
					ref={listRef}
					style={{ padding: `0 ${spacings.small}` }}
					threshold={20}
					variableSize
					width="100%"
				>
					{TransactionCardsRow}
				</List>
			</TransactionCardsContext.Provider>
		);
	}

	return <S.ListContainer ref={listContainerRef}>{list}</S.ListContainer>;
};

export default TransactionCardsByDay;
